import {
  Box,
  Button,
  DropdownCreatableV5,
  Modal,
  Text,
  Colors,
  Tooltip,
  InputV2,
} from '@pinchenterprisesnpm/friday-ui'
import RichTextEditor from '@src/ui/components/common/RichTextEditor'
import { usePhysicalRequirements } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/hooks/usePhysicalRequirements'
import { usePosition } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/hooks/usePosition'
import { useRichFields } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/hooks/useRichFields'
import { PhysicalRequirements } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/modals/edit-details-modal/components/physical-requirements/physical-requirements'
import { IPositionNameSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/position-name-section/position-name-section.types'
import {
  FooterWrapper as Footer,
  StableScrollableModalContent as Content,
} from 'ui/components/common/style'

import { IDropdownData } from '@src/models/Common'
import { connect, FormikProps } from 'formik'
import { SHIFT_PERKS_TOOLTIP } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/constants'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import {
  selectShiftPostV2CopyEditInitialValues,
  selectShiftPostV2InitialValues,
} from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'

const { Header } = Modal

interface IEditDetailsModalProps {
  isOpen: boolean
  closeModal: () => void
  formik?: FormikProps<IPositionNameSectionFormValues>
  sourcePage?: ShiftPostPage
}

export const EditDetailsModal = connect<IEditDetailsModalProps>(
  ({ isOpen, closeModal, formik, sourcePage }) => {
    const { values, handleBlur, setFieldValue } = formik

    // Get businessEntity and initialValues from Redux
    const businessEntity = useSelector(selectBusinessEntity)
    const copyEditInitialValues = useSelector(selectShiftPostV2CopyEditInitialValues)
    const postInitialValues = useSelector(selectShiftPostV2InitialValues)

    const initialValues =
      sourcePage === ShiftPostPage.POST_SHIFT ? postInitialValues : copyEditInitialValues

    // Perks field state
    const [perks, setPerks] = useState<string>(values?.description_short || '')

    // Keep perks in sync with formik values
    useEffect(() => {
      setPerks(values?.description_short || '')
    }, [values?.description_short])

    const resetPerks = () => {
      setPerks(values?.description_short || '')
    }

    const setPerksInFormik = () => {
      setFieldValue('description_short', perks)
    }

    const {
      descriptionRichText,
      specialInstructionsRichText,
      whatToWearRichText,
      travelTipsRichText,
      fieldErrors,
      updateRichTextField,
      resetRichTextFields,
      setRichTextFieldsInFormik,
    } = useRichFields(formik)

    const {
      amountLiftedFrequently,
      amountLiftedOccasionally,
      amountOfTimeSpentOnFeet,
      updatePhysicalRequirements,
      resetPhysicalRequirements,
      setPhysicalRequirementsInFormik,
      physicalReqsErrors,
    } = usePhysicalRequirements(formik)

    const {
      isLoading,
      handlePositionChange,
      handleCreatePosition,
      positionOptions,
      selectedPosition,
      resetPosition,
      setPositionInFormik,
    } = usePosition({ formik, businessEntity, initialValues })

    const resetFields = () => {
      resetRichTextFields()
      resetPhysicalRequirements()
      resetPosition()
      resetPerks()
    }

    const handleClose = () => {
      resetFields()
      closeModal()
    }

    // Set the fields in the formik
    const handleSave = () => {
      setRichTextFieldsInFormik()
      setPhysicalRequirementsInFormik()
      setPositionInFormik()
      setPerksInFormik()

      closeModal()
    }

    const hasFormErrors =
      Object.values(fieldErrors).some((error) => error) ||
      Object.values(physicalReqsErrors).some((error) => error)

    return (
      <Modal isOpen={isOpen} closeModal={handleClose} width='800px' height='max-content'>
        <Header title='Shift Details' />

        <Content>
          <Text mt='md' mb='sm' color='secondary'>
            You can edit requirements for this shift.
          </Text>

          <Text fontSize='medium' mb='xs'>
            Position Name
          </Text>

          <DropdownCreatableV5
            value={selectedPosition}
            placeholder='Please select a position...'
            isOutlined
            rounded={true}
            size='large'
            options={positionOptions}
            onChange={(selectedOption: IDropdownData) => {
              handlePositionChange(selectedOption)
            }}
            onCreateOption={handleCreatePosition}
            isDisabled={isLoading}
            borderColor={Colors.border}
            my='sm'
          />

          <Box>
            <Tooltip text={SHIFT_PERKS_TOOLTIP} trigger='mouseenter'>
              <Text fontSize='medium' mb='xs'>
                Perks
              </Text>
            </Tooltip>

            <InputV2
              value={perks}
              placeholder='Perks'
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPerks(e.target.value)
              }}
              style={{
                borderRadius: '10px',
                borderColor: Colors.border,
                marginTop: '12px',
                marginBottom: '12px',
              }}
            />
          </Box>

          <Box>
            <Text fontSize='medium' mb='xs'>
              Description *
            </Text>

            <RichTextEditor
              name='descriptionRichText'
              value={descriptionRichText}
              handleChange={updateRichTextField}
              handleBlur={handleBlur}
              errorMessage={fieldErrors.descriptionRichText}
              fieldTouched={true}
              borderRadius='10px'
              borderColor={Colors.border}
            />
          </Box>

          <Box>
            <Text fontSize='medium' mb='xs'>
              What to Wear *
            </Text>

            <RichTextEditor
              name='whatToWearRichText'
              value={whatToWearRichText}
              handleChange={updateRichTextField}
              handleBlur={handleBlur}
              errorMessage={fieldErrors.whatToWearRichText}
              fieldTouched={true}
              borderRadius='10px'
              borderColor={Colors.border}
            />
          </Box>

          <Box>
            <Text fontSize='medium' mb='xs'>
              Special Instructions
            </Text>

            <Text mb='sm' mt='xs' color='secondary' fontSize='small'>
              (not visible until worker accepted)
            </Text>

            <RichTextEditor
              name='specialInstructionsRichText'
              value={specialInstructionsRichText}
              handleChange={updateRichTextField}
              handleBlur={handleBlur}
              borderRadius='10px'
              borderColor={Colors.border}
            />
          </Box>

          <Box>
            <Text fontSize='medium' mb='xs'>
              Travel Tips
            </Text>

            <Text mb='sm' mt='xs' color='secondary' fontSize='small'>
              (not visible until worker accepted)
            </Text>

            <RichTextEditor
              name='travelTipsRichText'
              value={travelTipsRichText}
              handleChange={updateRichTextField}
              handleBlur={handleBlur}
              borderRadius='10px'
              borderColor={Colors.border}
            />
          </Box>

          <PhysicalRequirements
            amountLiftedFrequently={amountLiftedFrequently}
            amountLiftedOccasionally={amountLiftedOccasionally}
            amountOfTimeSpentOnFeet={amountOfTimeSpentOnFeet}
            updatePhysicalRequirements={updatePhysicalRequirements}
            physicalReqsErrors={physicalReqsErrors}
          />
        </Content>

        <Footer>
          <Button isFilledOutlined size='large' onClick={handleClose}>
            Cancel
          </Button>
          <Button size='large' onClick={handleSave} isDisabled={hasFormErrors}>
            Save
          </Button>
        </Footer>
      </Modal>
    )
  }
)
